import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button, Badge } from "react-bootstrap";
import { getUserState } from "../../../app/selector";
import { Preview } from 'react-dnd-multi-backend';
import { ItemTypes } from "./ItemTypes";
import { DropTarget } from "./DropTarget";
import { DragSource } from "./DragSource";
import { ReactComponent as Do } from "../../../resources/do.svg";
import { ReactComponent as DoNot } from "../../../resources/do-not.svg";
import { ReactComponent as Next } from "../../../resources/caret-right-fill.svg";
import { ReactComponent as Prev } from "../../../resources/caret-left-fill.svg";
import { BACKEND_URL } from "constants";
import PhotoThumbnail from "pages/common/photo/PhotoThumbnail";

// Import example icons
import manIcon from "../../../resources/Man Confirmation Icon.PNG";
import womanIcon from "../../../resources/Woman Confirmation Icon.jpg";

const Requests = () => {
  const navigate = useNavigate();
  const { user } = useSelector(getUserState);

  const [requests, setRequests] = useState(undefined);
  const [index, setIndex] = useState(0);

  const request = requests && requests.length > 0 ? requests[index] : null;

  const generatePreview = ({ itemType, item, style }) => {
    if (itemType !== ItemTypes.DRAG_SOURCE) {
      return null;
    }

    return (
      <div style={{ ...style, zIndex: 9999, opacity: 0.8 }}>
        <div className="shadow-hover" style={{ margin: 0 }}>
          {item.photo}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (requests === undefined && user) {
      const payload = {
        user_id: user.user_id,
      };

      axios
        .post(BACKEND_URL + "/request/check", payload)
        .then((response) => {
          setRequests(response.data || []);
        })
        .catch((e) => {
          console.error(e);
          setRequests([]);
        });
    }
  }, [requests, user]);

  useEffect(() => {
    if (request && !request.request_read) {
      const payload = {
        request_id: request.request_id,
      };

      axios
        .post(BACKEND_URL + "/request/request-read", payload)
        .then(() => {
          request.request_read = 1;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }, [index, request]);

  const handleDelete = (reqToDelete) => {
    const payload = {
      request_id: reqToDelete.request_id,
    };

    axios
      .post(BACKEND_URL + "/request/delete", payload)
      .then(() => {
        navigate(0);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const renderRequest = (currentReq) => {
    const receiverPhoto = (
      <PhotoThumbnail
        className="shadow-hover"
        src={`data:image/jpeg;base64,${currentReq.receiver_photo}`}
        photoId={currentReq.receiver_photo_id}
      />
    );

    return (
      <div className="request" key={currentReq.request_id}>
        <div className="receiver">
          <span className="receiver-placeholder"></span>
          <span className="receiver-placeholder-2"></span>

          {currentReq.accepted ? (
            <span className="receiver-placeholder"></span>
          ) : (
            <DragSource photo={receiverPhoto} requestId={currentReq.request_id} />
          )}
        </div>

        <div className="sender">
          <PhotoThumbnail
            className="shadow-hover"
            src={`data:image/jpeg;base64,${currentReq.sender_photo}`}
            photoId={currentReq.sender_photo_id}
          />

          <Do className="do" width="64" height="64" />

          {currentReq.accepted ? (
            receiverPhoto
          ) : (
            <DropTarget />
          )}
        </div>
      </div>
    );
  };

  const renderRequests = (requestsArray) => {
    if (!requestsArray) return null;

    const currentReq = requestsArray[index];

    let dateString = currentReq.creation_date.replace(" ", "T");
    if (!dateString.match(/(Z|[+-]\d{2}:\d{2})$/)) {
        dateString += "Z";
    }
    const localDate = new Date(dateString);

    const timestamp = localDate.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });

    return (
      <div className="requests">
        <h4>
          <div className="top-bar">
            <div className="top-bar-badges">
              <Badge pill bg="secondary">
                {timestamp}
              </Badge>
            </div>

            <Badge pill bg="success">
              <b>
                {index + 1}/{requestsArray.length}
              </b>
            </Badge>
          </div>
        </h4>

        {renderRequest(currentReq)}

        <div className="action">
          <Button
            variant="outline-primary"
            className="round-button-l"
            disabled={index <= 0}
            onClick={() => setIndex(index - 1)}
          >
            <Prev width="64" height="64" />
          </Button>

          <Button
            variant="outline-danger"
            className="round-button-l"
            onClick={() => handleDelete(currentReq)}
          >
            <DoNot width="64" height="64" />
          </Button>

          <Button
            variant="outline-primary"
            className="round-button-l"
            disabled={index >= requestsArray.length - 1}
            onClick={() => setIndex(index + 1)}
          >
            <Next width="64" height="64" />
          </Button>
        </div>
      </div>
    );
  };

  const renderEmptyState = () => {
    return (
      <div className="empty-state mt-4">
        <p className="empty-state-text text-muted">
          This is an example for a request. You can only see, as long as you don't have any requests. For a better chance to get a request, upload a few photos to your profile.
        </p>

        <div className="request request-mock">
          <div className="receiver">
            <span className="receiver-placeholder"></span>
            <span className="receiver-placeholder-2"></span>

            <div className="drag-source">
              <div className="shadow-hover">
                <img src={womanIcon} alt="Woman Example" />
              </div>
            </div>
          </div>

          <div className="sender">
            <div className="shadow-hover">
              <img src={manIcon} alt="Man Example" />
            </div>

            <Do className="do" width="64" height="64" />

            <div className="drop-target shadow-hover mock-drop-target">
              <span>Drop your photo to accept!</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="requests-page content-block flex bg-light p-4 rounded">
      <h1 className="text-center menu-title">Incoming Requests</h1>
      
      {/* Loading State
      {requests === undefined && (
        <div className="text-center mt-5">
          <h4 className="text-muted">Loading...</h4>
        </div>
      )} */}

      {/* Empty State Mock */}
      {requests !== undefined && requests.length === 0 && renderEmptyState()}

      {/* Render Real Requests */}
      {requests?.length > 0 && renderRequests(requests)}
      
      <Preview>{generatePreview}</Preview>
    </div>
  );
};

export default Requests;